/**
 * Parallax starfield backdrop.
 *
 * Three layers, each baked once into an offscreen tile and then blitted with a wrapped
 * offset — so the per-frame cost is three `drawImage` calls plus a short twinkle pass,
 * regardless of how many stars there are. Re-baking only happens on resize.
 *
 * The deepest layer carries two soft nebula clouds and moves least; the nearest layer is
 * sparse, bright and moves most. That depth cue is what stops the board from feeling like
 * hexes floating on flat black.
 */

import { withAlpha } from './palette';

interface Layer {
  canvas: CanvasImageSource | null;
  /** Tile dimensions in CSS pixels. */
  width: number;
  height: number;
  /** Parallax factor: 0 = fixed to the camera, 1 = moves with the world. */
  depth: number;
}

/** The brightest few stars get a twinkle; anything more is visual noise. */
interface Twinkler {
  x: number;
  y: number;
  radius: number;
  color: string;
  /** Phase offset so they do not blink in unison. */
  phase: number;
  speed: number;
  depth: number;
}

const LAYER_DEPTHS = [0.12, 0.32, 0.62] as const;

export class Starfield {
  private layers: Layer[] = LAYER_DEPTHS.map((depth) => ({
    canvas: null, width: 0, height: 0, depth,
  }));

  private twinklers: Twinkler[] = [];
  private width = 0;
  private height = 0;
  private dpr = 1;

  constructor(private readonly seed: number) {}

  /* --------------------------------------------------------------------- bake */

  resize(width: number, height: number, dpr: number): void {
    // A tile larger than the viewport means the wrap seam is usually off-screen, and the
    // 1.5× headroom covers parallax drift at low zoom.
    const tileW = Math.ceil(width * 1.5);
    const tileH = Math.ceil(height * 1.5);

    if (this.width === width && this.height === height && this.dpr === dpr) return;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.twinklers = [];

    this.layers.forEach((layer, index) => {
      layer.width = tileW;
      layer.height = tileH;
      layer.canvas = this.bakeLayer(index, tileW, tileH, dpr);
    });
  }

  private rng(salt: number): () => number {
    let state = (this.seed ^ Math.imul(salt + 1, 0x9e3779b9)) >>> 0;
    return () => {
      state = (state + 0x6d2b79f5) | 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  private makeSurface(w: number, h: number, dpr: number): {
    canvas: CanvasImageSource; ctx: CanvasRenderingContext2D;
  } | null {
    const pw = Math.max(1, Math.ceil(w * dpr));
    const ph = Math.max(1, Math.ceil(h * dpr));

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(pw, ph);
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
      if (ctx) {
        ctx.scale(dpr, dpr);
        return { canvas, ctx };
      }
    }
    if (typeof document === 'undefined') return null;

    const canvas = document.createElement('canvas');
    canvas.width = pw;
    canvas.height = ph;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(dpr, dpr);
    return { canvas, ctx };
  }

  private bakeLayer(
    index: number,
    w: number,
    h: number,
    dpr: number,
  ): CanvasImageSource | null {
    const s = this.makeSurface(w, h, dpr);
    if (!s) return null;
    const { ctx } = s;
    const rand = this.rng(index * 977);

    // Nebulae live on the deepest layer only, so they read as genuinely far away.
    if (index === 0) {
      const hues = [
        { color: '#4a3aa8', alpha: 0.2 },
        { color: '#2a6d8a', alpha: 0.16 },
      ];
      for (const hue of hues) {
        const cx = rand() * w;
        const cy = rand() * h;
        const radius = Math.max(w, h) * (0.34 + rand() * 0.22);
        const cloud = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        cloud.addColorStop(0, withAlpha(hue.color, hue.alpha));
        cloud.addColorStop(0.55, withAlpha(hue.color, hue.alpha * 0.42));
        cloud.addColorStop(1, withAlpha(hue.color, 0));
        ctx.fillStyle = cloud;
        ctx.fillRect(0, 0, w, h);
      }
    }

    // Star counts thin out toward the camera; near stars are fewer and bigger.
    const area = (w * h) / (1920 * 1080);
    const counts = [420, 260, 120];
    const count = Math.round((counts[index] ?? 200) * Math.max(0.4, area));

    for (let i = 0; i < count; i++) {
      const x = rand() * w;
      const y = rand() * h;
      // Cubed distribution: mostly faint pinpricks with a few standouts.
      const magnitude = rand() ** 3;
      const radius = (0.5 + magnitude * 1.9) * (1 + index * 0.5);
      const brightness = 0.28 + magnitude * 0.68;

      // Slight colour temperature spread — a pure-white starfield looks synthetic.
      const temp = rand();
      const color = temp < 0.16
        ? `rgba(180, 205, 255, ${brightness})`   // hot blue
        : temp > 0.86
          ? `rgba(255, 214, 180, ${brightness})` // cool orange
          : `rgba(240, 245, 255, ${brightness})`;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      // Faint bloom around the brighter ones.
      if (magnitude > 0.55) {
        const halo = ctx.createRadialGradient(x, y, 0, x, y, radius * 5);
        halo.addColorStop(0, `rgba(220, 232, 255, ${brightness * 0.28})`);
        halo.addColorStop(1, 'rgba(220, 232, 255, 0)');
        ctx.fillStyle = halo;
        ctx.fillRect(x - radius * 5, y - radius * 5, radius * 10, radius * 10);
      }
    }

    // A handful of showpiece stars with a diffraction cross.
    const featured = index === 2 ? 5 : 3;
    for (let i = 0; i < featured; i++) {
      const x = rand() * w;
      const y = rand() * h;
      const scale = 2.2 + rand() * 2.6;
      const color = rand() < 0.4 ? 'rgba(190, 214, 255, ' : 'rgba(255, 248, 232, ';

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = `${color}0.55)`;
      ctx.lineWidth = scale * 0.34;
      ctx.lineCap = 'round';
      // Four-point cross: long horizontal/vertical spikes, short diagonals.
      for (const [dx, dy, len] of [
        [1, 0, 5.5], [0, 1, 5.5], [0.7, 0.7, 2.2], [0.7, -0.7, 2.2],
      ] as const) {
        ctx.beginPath();
        ctx.moveTo(x - dx * scale * len, y - dy * scale * len);
        ctx.lineTo(x + dx * scale * len, y + dy * scale * len);
        ctx.stroke();
      }
      ctx.fillStyle = `${color}0.95)`;
      ctx.beginPath();
      ctx.arc(x, y, scale * 0.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      this.twinklers.push({
        x, y, radius: scale * 0.85, color,
        phase: rand() * Math.PI * 2,
        speed: 0.0008 + rand() * 0.0016,
        depth: LAYER_DEPTHS[index] ?? 0.3,
      });
    }

    return s.canvas;
  }

  /* --------------------------------------------------------------------- draw */

  draw(
    ctx: CanvasRenderingContext2D,
    cameraX: number,
    cameraY: number,
    zoom: number,
    timeMs: number,
  ): void {
    ctx.save();
    ctx.fillStyle = '#07080f';
    ctx.fillRect(0, 0, this.width, this.height);

    for (const layer of this.layers) {
      if (!layer.canvas) continue;

      // Parallax: deeper layers translate less. Zoom scales the effect so pinching in
      // does not make the background slide unnaturally fast.
      const offsetX = -cameraX * layer.depth * zoom;
      const offsetY = -cameraY * layer.depth * zoom;

      // Wrap into [0, tile) then start one tile back, so the field tiles seamlessly in
      // both directions without any conditional edge handling.
      const startX = (offsetX % layer.width) - layer.width;
      const startY = (offsetY % layer.height) - layer.height;

      for (let x = startX; x < this.width; x += layer.width) {
        for (let y = startY; y < this.height; y += layer.height) {
          ctx.drawImage(layer.canvas, x, y, layer.width, layer.height);
        }
      }
    }

    // Twinkle pass: a few additive discs whose alpha breathes. Cheap, and avoids
    // re-baking a whole layer to animate five stars.
    ctx.globalCompositeOperation = 'lighter';
    for (const star of this.twinklers) {
      const pulse = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(timeMs * star.speed + star.phase));
      const offsetX = -cameraX * star.depth * zoom;
      const offsetY = -cameraY * star.depth * zoom;
      const layer = this.layers.find((l) => l.depth === star.depth);
      if (!layer) continue;

      // Match the tiling used above so the twinkle lands on its star.
      const wrapX = ((star.x + offsetX) % layer.width + layer.width) % layer.width;
      const wrapY = ((star.y + offsetY) % layer.height + layer.height) % layer.height;

      for (const dx of [0, -layer.width]) {
        for (const dy of [0, -layer.height]) {
          const px = wrapX + dx;
          const py = wrapY + dy;
          if (px < -20 || px > this.width + 20 || py < -20 || py > this.height + 20) continue;
          ctx.globalAlpha = pulse * 0.5;
          ctx.fillStyle = `${star.color}1)`;
          ctx.beginPath();
          ctx.arc(px, py, star.radius * (0.7 + pulse * 0.5), 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }
}
